import { IAlreadyConnectedPersonToManagerData } from "../../interfaces/error/IAlreadyAttachPersonToManagerData";
import { IManager } from "../../interfaces/IManager";
import { DefaultServerResponse } from "../../interfaces/store/api/APIInterfaces";
import {
  EditManagerResponse,
  PinToManagerParams,
  PinToManagersResponse,
  TransformResponseSuccessCreateManager,
} from "../../interfaces/store/api/listManager/IListManager";
import { IFixPersonToManager } from "../../interfaces/store/slices/listAgentSlice";
import { IManagerInfoAdmin } from "../../interfaces/store/slices/managerSlice/IManagerInfoAdmin";
import { EditManagerResponseParams } from "../../types/store/api/AdminManager/EditManagerResponseParams";
import { IAgent } from "../slice/listAgentSlice/listAgentSlice";
import { api } from "./apiCore";
import { permissionApi } from "./permission.api";

interface IFindAgentResponse {
  message: string;
  success: boolean;
  data: IAgent;
}

interface IAddManagerResponse {
  message: string;
  success: boolean;
  data: IManager;
}

interface IManagerArchive {
  manager_id: number;
  is_enabled: boolean;
}

// для менеджеров
export interface IApiManagerRequest {
  id?: number;
  surname?: string;
  name?: string;
  patronymic?: string;
  email?: string;
  phone_number?: string;
  password?: string;
  password_confirmation?: string;
}

export interface ResponseSuccessCreateManager extends DefaultServerResponse {
  data: {
    name: string;
    surname: string;
    phone_number: string;
    email: string;
    type: string; // Если есть другие возможные значения кроме "manager", добавьте их.
    email_verified_at: string; //  Предполагаем, что это строка в формате даты/времени ISO.
    id: number;
  };
}

export const listAgentApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addManager: builder.mutation<
      TransformResponseSuccessCreateManager,
      Omit<IApiManagerRequest, "id">
    >({
      query: (value) => ({
        url: `/auth/admin/users`,
        method: "POST",
        body: { ...value, type: "manager" },
      }),

      transformResponse: (res: ResponseSuccessCreateManager) => {
        return {
          ...res,
          data: {
            agents: [],
            contacts: [],
            created_at: res.data.email_verified_at,
            email: res.data.email,
            id: res.data.id,
            image_path: "",
            inn: null,
            is_banned: false,
            is_enabled: true,
            name: res.data.name,
            organization: null,
            organization_id: null,
            password: "",
            patronymic: "",
            phone_number: res.data.phone_number,
            position: null,
            requisites: null,
            role_id: null,
            special: false,
            surname: res.data.surname,
            type: "manager",
            user_contract: [],
          },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;

          dispatch(
            permissionApi.util.updateQueryData(
              "getPermission",
              undefined,
              (draft) => {
                if (draft.data.manager) {
                  const newManagerData = response.data;

                  draft.data.manager.push(newManagerData);
                }
              },
            ),
          );
        } catch (e) {}
      },
    }),
    // найти агента по инн
    findAgent: builder.mutation<
      IFindAgentResponse,
      { manager_id: number; inn: string }
    >({
      query: (value) => ({
        url: `/auth/admin/users/searchAgent/${value.manager_id}/${value.inn}`,
        method: "POST",
      }),
    }),
    // удалить агента у менеджера
    deleteAgent: builder.mutation<IFindAgentResponse, number>({
      query: (value) => ({
        url: `/auth/admin/users/${value}`,
        method: "DELETE",
      }),
    }),

    attachToManager: builder.mutation<
      PinToManagersResponse,
      PinToManagerParams
    >({
      query: (value) => ({
        url: `/auth/admin/users/connect/${value.managerId}`,
        method: "POST",
        body: { inn: value.inn },
      }),

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;

          if (response) {
            dispatch(
              permissionApi.util.updateQueryData(
                "getPermission",
                undefined,
                (draft) => {
                  if (
                    response.data.partner_id.type === "agent" &&
                    draft.data.manager
                  ) {
                    for (let i = 0; i < draft.data.manager.length; i++) {
                      if (draft.data.manager[i].id === arg.managerId) {
                        const agentData: IFixPersonToManager = {
                          id: response.data.partner_id.id,
                          organization_id:
                            response.data.partner_id.organization_id,
                          organization: response.data?.partner_id?.organization,
                          role_id: response.data.partner_id?.role_id,
                          surname: response.data.partner_id.surname,
                          name: response.data.partner_id.name,
                          patronymic: response.data.partner_id.patronymic || "",
                          email: response.data.partner_id.email,
                          image_path: response.data.partner_id.image_path,
                          inn: response.data.partner_id.inn,
                          phone_number: response.data.partner_id.phone_number,
                          type: "agent",
                          is_banned: response.data.partner_id.is_banned,
                          is_enabled: response.data.partner_id.is_banned,
                          position: response.data.partner_id?.position,
                          connect_data: new Date().toISOString(),
                        };

                        draft.data.manager[i].agents.push(agentData);
                        break;
                      }
                    }
                  }

                  if (
                    response.data.partner_id.type === "client" &&
                    draft.data.manager
                  ) {
                    for (let i = 0; i < draft.data.manager.length; i++) {
                      if (draft.data.manager[i].id === arg.managerId) {
                        const clientData: IFixPersonToManager = {
                          id: response.data.partner_id.id,
                          organization_id:
                            response.data.partner_id.organization_id,
                          organization: response.data.partner_id.organization,
                          role_id: response.data.partner_id.role_id,
                          surname: response.data.partner_id.surname,
                          name: response.data.partner_id.name,
                          patronymic: response.data.partner_id.patronymic || "",
                          email: response.data.partner_id.email,
                          image_path: response.data.partner_id.image_path,
                          inn: response.data.partner_id.inn,
                          phone_number: response.data.partner_id.phone_number,
                          type: "client",
                          is_banned: response.data.partner_id.is_banned,
                          is_enabled: response.data.partner_id.is_banned,
                          position: response.data.partner_id.position,
                          connect_data: new Date().toISOString(),
                        };

                        draft.data.manager[i].clients?.push(clientData);
                        break;
                      }
                    }
                  }
                },
              ),
            );
          }
        } catch (e) {}
      },
      transformErrorResponse(baseQueryReturnValue) {
        const alreadyConnectPersonErrorData = baseQueryReturnValue?.data?.data;

        if (
          baseQueryReturnValue.status === 400 &&
          alreadyConnectPersonErrorData &&
          typeof alreadyConnectPersonErrorData === "object" &&
          typeof alreadyConnectPersonErrorData.surname === "string" &&
          typeof alreadyConnectPersonErrorData.name === "string" &&
          typeof alreadyConnectPersonErrorData.id === "number" &&
          alreadyConnectPersonErrorData.partner_id
        ) {
          const transformErrorData: IAlreadyConnectedPersonToManagerData = {
            managerInfo: {
              surname: alreadyConnectPersonErrorData.surname,
              name: alreadyConnectPersonErrorData.name,
              patronymic: alreadyConnectPersonErrorData.patronymic || "",
              id: alreadyConnectPersonErrorData.id,
            },
            personData: alreadyConnectPersonErrorData.partner_id,
          };
          return {
            status: baseQueryReturnValue.status,
            data: transformErrorData,
          };
        }

        return baseQueryReturnValue;
      },
    }),
    reattachToManager: builder.mutation<
      DefaultServerResponse,
      {
        fromManagerId: string | number;
        toManagerId: string | number;
        humanIds: number[];
        type: "agent" | "client";
      }
    >({
      query: (value) => ({
        url: `/auth/admin/users/reconnect/${value.toManagerId}`,
        method: "POST",
        body: { ids: value.humanIds },
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;

          dispatch(
            permissionApi.util.updateQueryData(
              "getPermission",
              undefined,
              (draft) => {
                let fromManagerItem: IManagerInfoAdmin | null = null;
                let toManagerItem: IManagerInfoAdmin | null = null;

                for (let i = 0; i < draft.data.manager.length; i++) {
                  if (arg.fromManagerId === draft.data.manager[i].id) {
                    fromManagerItem = draft.data.manager[i];
                  }

                  if (arg.toManagerId === draft.data.manager[i].id) {
                    toManagerItem = draft.data.manager[i];
                  }

                  if (fromManagerItem && toManagerItem) break;
                }

                if (fromManagerItem && toManagerItem) {
                  if (arg.type === "agent") {
                    const setAgents = new Set(arg.humanIds);

                    const reconnectAgents = fromManagerItem.agents.filter(
                      (el) => setAgents.has(el.id),
                    );

                    toManagerItem.agents.push(...reconnectAgents);
                    fromManagerItem.agents = fromManagerItem.agents.filter(
                      (el) => !setAgents.has(el.id),
                    );
                  }

                  if (
                    arg.type === "client" &&
                    fromManagerItem.clients &&
                    toManagerItem.clients
                  ) {
                    const setClients = new Set(arg.humanIds);

                    const reconnectClients = fromManagerItem.clients.filter(
                      (el) => setClients.has(el.id),
                    );

                    toManagerItem.clients?.push(...reconnectClients);
                    fromManagerItem.clients = fromManagerItem.clients.filter(
                      (el) => !setClients.has(el.id),
                    );
                  }
                }
              },
            ),
          );
        } catch {}
      },
    }),

    detachPersonFromManager: builder.mutation<
      DefaultServerResponse,
      { managerId: number; humanId: number }
    >({
      query: (value) => ({
        url: `/auth/admin/users/${value.humanId}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            permissionApi.util.updateQueryData(
              "getPermission",
              undefined,
              (draft) => {
                if (draft.data.manager) {
                  for (let i = 0; i < draft.data.manager.length; i++) {
                    if (draft.data.manager[i].id === Number(arg.managerId)) {
                      draft.data.manager[i].agents = draft.data.manager[
                        i
                      ].agents.filter((el) => {
                        return el.id !== arg.humanId;
                      });
                    }
                  }
                }
              },
            ),
          );
        } catch (e) {}
      },
    }),

    editManager: builder.mutation<
      EditManagerResponse,
      EditManagerResponseParams
    >({
      query: (value) => ({
        url: `/auth/admin/users/${value.id}`,
        method: "PUT",
        body: {
          surname: value.surname,
          name: value.name,
          patronymic: value.patronymic,
          email: value.email,
          phone_number: value.phone_number,
          password: value.password,
        },
      }),
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          if (data) {
            dispatch(
              permissionApi.util.updateQueryData(
                "getPermission",
                undefined,
                (draft) => {
                  const editManagerInfo = draft.data.manager.find(
                    (el) => arg.id === el.id,
                  );

                  if (editManagerInfo) {
                    editManagerInfo.surname = arg.surname;
                    editManagerInfo.name = arg.name;
                    editManagerInfo.patronymic = arg.patronymic;
                    editManagerInfo.email = arg.email;
                    editManagerInfo.phone_number = arg.phone_number;
                    editManagerInfo.password = arg.password;
                  }
                },
              ),
            );
          }
        } catch (e) {}
      },
    }),

    blockManager: builder.mutation<
      DefaultServerResponse,
      { managerId: string | number }
    >({
      query: ({ managerId }) => ({
        url: `/auth/admin/users/block/${managerId}`,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            permissionApi.util.updateQueryData(
              "getPermission",
              undefined,
              (draft) => {
                if (draft.data.manager) {
                  for (let i = 0; i < draft.data.manager.length; i++) {
                    if (draft.data.manager[i].id === Number(arg.managerId)) {
                      draft.data.manager[i].is_banned = true;
                      break;
                    }
                  }
                }
              },
            ),
          );
        } catch (e) {}
      },
    }),
    archiveManager: builder.mutation<DefaultServerResponse, IManagerArchive>({
      query: ({ manager_id, is_enabled }) => ({
        url: `/auth/user/${manager_id}/enable`,
        method: "PATCH",
        body: { is_enabled },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            permissionApi.util.updateQueryData(
              "getPermission",
              undefined,
              (draft) => {
                if (draft.data.manager) {
                  for (let i = 0; i < draft.data.manager.length; i++) {
                    if (draft.data.manager[i].id === arg.manager_id) {
                      draft.data.manager[i].is_enabled = arg.is_enabled;
                      break;
                    }
                  }
                }
              },
            ),
          );
        } catch (e) {}
      },
    }),
  }),
});

export const {
  useAddManagerMutation,
  useFindAgentMutation,
  useEditManagerMutation,
  useArchiveManagerMutation,
  useDeleteAgentMutation,
  useAttachToManagerMutation,
  useReattachToManagerMutation,
  useDetachPersonFromManagerMutation,
  useBlockManagerMutation,
} = listAgentApi;
